import ImagePreviewCarousel, {
  IImagePreviewItem,
} from "@/components/ImagePreviewCarousel";
import { H3, H5, HeaderTextBlock, P } from "@/components/Typography";
import { SplitLayout } from "@/layouts";
import { NAVIGATE_FORWARDS } from "@/utils/const";
import { Trans, useTranslation } from "react-i18next";
import { Alert } from "@/components/Alert";
import { Button } from "@/components/Button";
import { useState } from "react";
import { Modal } from "@/components/Modal";

import emailSoftOptIn from "@/assets/images/collect/email-widget-soft-opt-in.png";
import emailSoftOptOut from "@/assets/images/collect/email-widget-soft-opt-out.png";
import emailImpliedConsent from "@/assets/images/collect/email-widget-implied-consent.png";
import widgetModalContent from "@/assets/images/collect/widget-modal-content.png";
import { useNavigate } from "react-router-dom";
import { getOnboardingUrl } from "@/utils/onboardingManager";

const ShopifyPreview = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const toggleModal = () => setIsModalOpen(isModalOpen => !isModalOpen);

  const onClickDone = () => {
    navigate(getOnboardingUrl("SHOPIFY_PREVIEW"), {
      state: {
        direction: NAVIGATE_FORWARDS,
      },
    });
  };

  const images: IImagePreviewItem[] = [
    {
      title: t("emailOnboarding.shopifyPreview.right.images.softOptIn"),
      image: emailSoftOptIn,
    },
    {
      title: t("emailOnboarding.shopifyPreview.right.images.softOptOut"),
      image: emailSoftOptOut,
    },
    {
      title: t("emailOnboarding.shopifyPreview.right.images.impliedConsent"),
      image: emailImpliedConsent,
    },
  ];

  const renderDescription = () => (
    <div className="mt-10 flex flex-col gap-10">
      <Trans i18nKey="emailOnboarding.shopifyPreview.left.header.description">
        <P>
          <strong>zero</strong>
          one
        </P>
        <P>
          <strong>zero</strong>
          one
        </P>
      </Trans>
    </div>
  );

  const renderTrailingContent = () => (
    <Alert
      titleSlot={
        <H5 className="font-semibold">
          {t("emailOnboarding.shopifyPreview.right.trailingContent.title")}
        </H5>
      }>
      <div className="flex flex-col items-center gap-10 sm:flex-row">
        <div>
          {t(
            "emailOnboarding.shopifyPreview.right.trailingContent.description",
          )}
        </div>
        <div className="w-full sm:w-[unset]">
          <Button
            as="button"
            variant="secondary-outline"
            width="sm"
            onClick={toggleModal}>
            {t("emailOnboarding.shopifyPreview.right.trailingContent.cta")}
          </Button>
        </div>
      </div>
    </Alert>
  );

  const renderDoneButton = () => (
    <div className="mt-30 flex flex-row justify-end sm:mt-40">
      <div className="w-full md:w-[250px]">
        <Button as="button" variant="primary" onClick={onClickDone}>
          {t("emailOnboarding.shopifyPreview.right.cta")}
        </Button>
      </div>
    </div>
  );

  const renderLeftLayout = () => (
    <HeaderTextBlock
      align="left"
      featureText={t("emailOnboarding.shopifyPreview.left.header.feature")}
      headingText={t("emailOnboarding.shopifyPreview.left.header.heading")}
      customHtml={renderDescription()}
      customHtmlPosition="bottom"
    />
  );

  const renderRightLayout = () => (
    <>
      <ImagePreviewCarousel
        images={images}
        imageTrailingContent={renderTrailingContent()}
        identifier="Widget Preview"
        hidePreviews={true}
      />
      {renderDoneButton()}
    </>
  );

  const renderModal = () => (
    <Modal isOpen={isModalOpen} toggleIsOpen={toggleModal}>
      <H3 className="mt-10 font-semibold">
        {t("emailOnboarding.shopifyPreview.right.modal.title")}
      </H3>
      <img src={widgetModalContent} className="max-w-full" />
    </Modal>
  );

  return (
    <>
      <SplitLayout
        leftPanel={renderLeftLayout()}
        rightPanel={renderRightLayout()}
        backButtonPath={getOnboardingUrl("SHOPIFY_WIDGET_INSTALLED")}
      />
      {renderModal()}
    </>
  );
};

export default ShopifyPreview;
